import { useEffect, useState, useRef, useCallback, type ChangeEvent } from "react";
import { useParams } from "react-router-dom";
import { getTrackingData, submitTrackingRating, uploadCarteGrise, type TrackingData } from "@services/api/tracking";
import CarteGriseUploadOverlay from "@components/CarteGriseUploadOverlay";
import type { TrackingStep } from "types/ordre";

import VehicleImage from "@components/ui/VehicleImage";
import Icon1 from "@assets/icons/Icon";

interface StepConfig {
  key: TrackingStep;
  title: string;
  subtitle: string;
  cta?: { label: string; action: string };
}

const ALL_STEPS: StepConfig[] = [
  {
    key: "carte_grise_manquante",
    title: "Carte grise manquante",
    subtitle: "En attente du depot de la carte grise",
    cta: { label: "Deposer ma carte grise", action: "deposit_carte_grise" },
  },
  {
    key: "en_attente_devis",
    title: "En attente du devis",
    subtitle: "Nous preparons votre devis",
  },
  {
    key: "devis_pret",
    title: "Devis pret",
    subtitle: "Le devis est disponible pour validation",
    cta: { label: "Voir le devis", action: "view_devis" },
  },
  {
    key: "en_attente_depot",
    title: "En attente du vehicule",
    subtitle: "Nous attendons le depot de votre vehicule",
  },
  {
    key: "vehicule_depose",
    title: "Vehicule depose",
    subtitle: "Votre vehicule est en cours d'analyse",
  },
  {
    key: "devis_supplementaire",
    title: "Devis supplementaire",
    subtitle: "Un devis supplementaire est en attente de signature",
    cta: { label: "Voir le devis", action: "view_devis" },
  },
  {
    key: "acompte_manquant",
    title: "Acompte en attente",
    subtitle: "En attente du reglement de l'acompte",
    cta: { label: "Regler mon acompte", action: "pay_acompte" },
  },
  {
    key: "reparations_en_cours",
    title: "Reparations en cours",
    subtitle: "Les travaux sur votre vehicule sont en cours",
  },
  {
    key: "travaux_termines",
    title: "Travaux termines",
    subtitle: "Votre vehicule est bientot pret",
  },
  {
    key: "solde_en_attente",
    title: "Paiement final en attente",
    subtitle: "En attente du reglement du solde",
    cta: { label: "Regler le solde", action: "pay_solde" },
  },
  {
    key: "paiement_valide",
    title: "Paiement valide",
    subtitle: "Paiement confirme",
  },
  {
    key: "vehicule_disponible",
    title: "Vehicule disponible",
    subtitle: "Votre vehicule est pret a etre recupere",
  },
  {
    key: "vehicule_restitue",
    title: "Vehicule restitue",
    subtitle: "Merci de votre confiance !",
  },
];

const MOBILITY_OPTIONS = [
  { key: "attente", label: "Je peux attendre sur place (1h30)" },
  { key: "velo", label: "Demander un vélo électrique (gratuit)" },
  { key: "pret", label: "Demander un véhicule de prêt (zone limitée)" },
  { key: "location", label: "Demander un véhicule de location (liberté/payant)" },
];

const TIRE_OPTIONS = [
  { key: "premier_prix", label: "Pneus premier prix" },
  { key: "intermediaires", label: "Pneus intermédiaires" },
  { key: "premium", label: "Pneus premium" },
];

// ─── Component ─────────────────────────────────────────────

type ScreenState = "loading" | "error" | "ready";

export default function TrackingScreen() {
  const { id } = useParams<{ id: string }>();
  const [state, setState] = useState<ScreenState>("loading");
  const [data, setData] = useState<TrackingData | null>(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [hoveredStar, setHoveredStar] = useState(0);
  const [selectedRating, setSelectedRating] = useState(0);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!id) { setErrorMsg("Lien invalide."); setState("error"); return; }
    getTrackingData(id)
      .then((d) => { setData(d); setSelectedRating(d.client_rating || 0); setState("ready"); })
      .catch(() => { setErrorMsg("Impossible de charger le suivi."); setState("error"); });
  }, [id]);

  // Poll for status updates while the ordre is not yet restituted
  useEffect(() => {
    if (!id || !data) return;
    const terminal = data.tracking_step === "vehicule_restitue";
    if (terminal) return;
    pollRef.current = setInterval(() => {
      getTrackingData(id).then((d) => {
        setData((prev) => {
          if (!prev) return d;
          // Update if tracking step or any flag changed
          if (
            d.tracking_step !== prev.tracking_step ||
            d.devis_requested !== prev.devis_requested ||
            d.devis_signed !== prev.devis_signed ||
            d.devis_supplementaire_requested !== prev.devis_supplementaire_requested ||
            JSON.stringify(d.additional_devis_groups) !== JSON.stringify(prev.additional_devis_groups) ||
            d.acompte_paid !== prev.acompte_paid ||
            d.solde_paid !== prev.solde_paid ||
            d.payment_link_url !== prev.payment_link_url ||
            d.devis_pdf_url !== prev.devis_pdf_url
          ) return d;
          return prev;
        });
      }).catch(() => {});
    }, 10000);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [id, data?.tracking_step]);

  if (state === "loading") {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-[#5C6BFF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (state === "error" || !data) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-4 p-5">
        <div className="text-[40px]">🔍</div>
        <p className="text-[13px] font-medium text-[#A1A1A1] text-center">{errorMsg}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col items-center">
      <div className="w-full max-w-[375px]">
        <TrackingContent
          data={data}
          ordreId={id!}
          hoveredStar={hoveredStar}
          setHoveredStar={setHoveredStar}
          selectedRating={selectedRating}
          setSelectedRating={setSelectedRating}
          onDataRefresh={() => {
            if (id) getTrackingData(id).then((d) => { setData(d); setSelectedRating(d.client_rating || 0); });
          }}
        />
      </div>
    </div>
  );
}

// ─── Reusable content (also used in preview modal) ──────────

export function TrackingContent({
  data,
  ordreId,
  hoveredStar,
  setHoveredStar,
  selectedRating,
  setSelectedRating,
  previewMode,
  onDataRefresh,
}: {
  data: TrackingData;
  ordreId?: string;
  hoveredStar: number;
  setHoveredStar: (v: number) => void;
  selectedRating: number;
  setSelectedRating: (v: number) => void;
  previewMode?: boolean;
  onDataRefresh?: () => void;
}) {
  const [carteGriseFile, setCarteGriseFile] = useState<File | null>(null);
  const carteGriseInputRef = useRef<HTMLInputElement>(null);

  const triggerCarteGriseInput = () => {
    carteGriseInputRef.current?.click();
  };

  const handleCarteGriseChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && ordreId) setCarteGriseFile(file);
    e.target.value = "";
  };

  const steps = ALL_STEPS.filter((s) => {
    if (s.key === "carte_grise_manquante" && data.has_carte_grise) return false;
    if (s.key === "devis_pret" && !data.devis_requested) return false;
    if (s.key === "en_attente_depot" && !data.devis_signed) return false;
    if (s.key === "devis_supplementaire" && !data.devis_supplementaire_requested) return false;
    if (s.key === "acompte_manquant" && !data.acompte_requested) return false;
    if (s.key === "solde_en_attente" && !data.solde_requested) return false;
    if (s.key === "paiement_valide" && !data.solde_paid) return false;
    return true;
  });

  const currentStepIndex = steps.findIndex((s) => s.key === data.tracking_step);
  const currentStep = steps[currentStepIndex] ?? steps[0];

  const isRestitue = data.tracking_step === "vehicule_restitue";

  const summaryRows = [
    { label: "Ordre", value: `#${data.ordre_num}` },
    { label: "Véhicule", value: data.vehicle_plate || "—" },
    { label: "Service", value: data.service_type || "—" },
    { label: "Type", value: data.intervention_type || "—" },
    { label: "Interventions", value: data.interventions?.join(", ") || "—" },
    { label: "Date de dépôt", value: data.deposit_date || "—" },
    { label: "Date de restitution", value: data.restitution_date || "—" },
  ];

  return (
    <div className="flex flex-col gap-5 p-5">
      {/* Header */}
      <div className="flex flex-col gap-[2px]">
        <span className="text-[11px] font-medium leading-4 text-[#A1A1A1]">
          Ordre #{data.ordre_num}
        </span>
        <span className="text-[13px] font-medium leading-5 text-[#5C6BFF]">
          Suivi de mon ordre
        </span>
      </div>

      {/* Car image */}
      <div className="flex items-center justify-center p-5 border border-[#D9D9D9] rounded-[10px] bg-white h-[156px]">
        <VehicleImage marque={data.vehicle_marque} modele={data.vehicle_modele} coloris={data.vehicle_coloris} alt="Véhicule" className="w-[280px] h-[116px]" />
      </div>

      {/* Progress bar — weighted step segments */}
      <div className="flex flex-col gap-[10px]">
        <div className="flex gap-[3px]">
          {steps.map((step, i) => {
            const done = currentStepIndex > i || isRestitue;
            const isActive = currentStepIndex === i && !isRestitue;
            return (
              <div key={step.key} className="flex-1 h-1 rounded-full overflow-hidden bg-[#E5E5E5]">
                {isActive ? (
                  <div className="h-full rounded-full bg-[#5C6BFF] animate-loading-bar" />
                ) : done ? (
                  <div className="h-full rounded-full bg-[#5C6BFF] w-full" />
                ) : null}
              </div>
            );
          })}
        </div>

        {/* Step info */}
        <div className="flex flex-col">
          <span className="text-[13px] font-medium leading-5 text-[#5C6BFF]">
            {currentStep.title}
          </span>
          <span className="text-[11px] font-medium leading-4 text-[#A1A1A1]">
            {currentStep.key === "travaux_termines" && !data.solde_requested
              ? "En attente de la facturation"
              : currentStep.key === "paiement_valide" && data.solde_paid
                ? "En attente de la restitution"
                : currentStep.subtitle}
          </span>
        </div>
      </div>

      {/* CTA button */}
      {currentStep.cta && !isRestitue && (() => {
        const action = currentStep.cta!.action;
        const isPayAction = action === "pay_acompte" || action === "pay_solde";
        const isDevisAction = action === "view_devis";
        const isCarteGriseAction = action === "deposit_carte_grise";
        const hasDevisLink = data.devis_estimate_token || data.devis_sign_token;
        const hasPaymentLink = !!data.payment_link_url;

        // Hide payment CTA if no link available
        if (isPayAction && !hasPaymentLink) return null;
        // Hide devis CTA if no token available
        if (isDevisAction && !hasDevisLink) return null;

        return (
          <>
            <button
              onClick={() => {
                if (previewMode) return;
                if (isCarteGriseAction) {
                  triggerCarteGriseInput();
                  return;
                }
                if (isDevisAction) {
                  if (data.devis_estimate_token) {
                    window.location.href = `/devis/${data.devis_estimate_token}`;
                    return;
                  }
                  if (data.devis_sign_token) {
                    window.location.href = `/sign/${data.devis_sign_token}`;
                    return;
                  }
                }
                if (isPayAction && data.payment_link_url) {
                  window.location.href = data.payment_link_url;
                  return;
                }
              }}
              className="flex items-center justify-center py-[15px] bg-black text-white font-bold text-[15px] leading-[100%] rounded-[10px] shadow-[0_2px_4px_rgba(0,0,0,0.15)] hover:bg-neutral-800 transition-colors"
            >
              {currentStep.cta!.label}
            </button>
          </>
        );
      })()}

      {/* Devis signature CTA — shown when a devis is pending signature */}
      {data.devis_sign_token && data.devis_sign_status === "pending" && currentStep.key !== "devis_pret" && (
        <button
          onClick={() => !previewMode && (window.location.href = `/sign/${data.devis_sign_token}`)}
          className="flex items-center justify-center gap-[10px] py-[15px] bg-[#5C6BFF] text-white font-bold text-[15px] leading-[100%] rounded-[10px] shadow-[0_2px_4px_rgba(0,0,0,0.15)] hover:bg-[#4B5AEE] transition-colors"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
            <path d="M14.5 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V8.5L14.5 3zM14 4l5 5h-5V4zM6 20V5h7v5h5v10H6z" />
            <path d="M9 13h6v1H9zM9 16h4v1H9z" />
          </svg>
          Signer le devis
        </button>
      )}

      {/* Carte grise uploaded — show name + replace button */}
      {data.has_carte_grise && (
        <div className="flex items-center gap-[8px] py-[8px] px-[12px] rounded-[8px] border border-[#E4E4E4] bg-[#FAFAFA]">
          <svg width="12" height="12" viewBox="0 0 16 16" fill="#5C6BFF">
            <path d="M6.5 11.5L3 8l1-1 2.5 2.5L12 4l1 1-6.5 6.5z" />
          </svg>
          <span className="text-[11px] font-medium text-[#5C6BFF] flex-1">
            {data.carte_grise_name || "Carte grise"} déposée
          </span>
          <button
            onClick={() => {
              if (previewMode) return;
              triggerCarteGriseInput();
            }}
            className="text-[11px] font-medium text-[#A1A1A1] hover:text-[#5C6BFF] transition-colors"
          >
            Modifier
          </button>
        </div>
      )}

      {/* Star rating (only on restitué step) — above status badges */}
      {isRestitue && (
        <RatingBlock
          hoveredStar={hoveredStar}
          setHoveredStar={setHoveredStar}
          selectedRating={selectedRating}
          setSelectedRating={setSelectedRating}
          ordreId={ordreId}
          previewMode={previewMode}
        />
      )}

      {/* Status badges */}
      {(data.initial_devis_signed || data.additional_devis_signed ||
        (data.additional_devis_groups && data.additional_devis_groups.length > 0) ||
        (data.devis_sign_status === "signed" || (data.devis_requested && data.devis_signed)) ||
        (data.acompte_requested && data.acompte_paid) ||
        (data.solde_requested && data.solde_paid)) && (
        <div className="flex flex-col gap-[6px]">
          {data.initial_devis_signed && (
            <StatusBadge
              label="Devis initial signe"
              onClick={data.initial_devis_token && !previewMode ? () => { window.location.href = `/devis/${data.initial_devis_token}`; } : undefined}
            />
          )}
          {/* Multiple supplementary devis badges */}
          {data.additional_devis_groups && data.additional_devis_groups.length > 0 ? (
            data.additional_devis_groups.map((g, idx) => (
              g.signed ? (
                <StatusBadge
                  key={g.key}
                  label={`Devis supplementaire${data.additional_devis_groups!.length > 1 ? ` #${idx + 1}` : ""} signe`}
                  onClick={g.token && !previewMode ? () => { window.location.href = `/devis/${g.token}`; } : undefined}
                />
              ) : data.initial_devis_signed ? (
                <StatusBadge
                  key={g.key}
                  label={`Devis supplementaire${data.additional_devis_groups!.length > 1 ? ` #${idx + 1}` : ""} en attente`}
                  pending
                />
              ) : null
            ))
          ) : (
            <>
              {data.devis_supplementaire_requested && data.additional_devis_signed && (
                <StatusBadge
                  label="Devis supplementaire signe"
                  onClick={data.additional_devis_token && !previewMode ? () => { window.location.href = `/devis/${data.additional_devis_token}`; } : undefined}
                />
              )}
              {data.devis_supplementaire_requested && !data.additional_devis_signed && data.initial_devis_signed && (
                <StatusBadge
                  label="Devis supplementaire en attente"
                  pending
                />
              )}
            </>
          )}
          {!data.initial_devis_signed && !data.additional_devis_signed &&
            !(data.additional_devis_groups && data.additional_devis_groups.length > 0) &&
            (data.devis_sign_status === "signed" || (data.devis_requested && data.devis_signed)) && (
            <StatusBadge
              label="Devis signe"
              onClick={data.devis_pdf_url && !previewMode ? () => window.open(data.devis_pdf_url, "_blank") : undefined}
            />
          )}
          {data.acompte_requested && data.acompte_paid && (
            <StatusBadge label="Acompte regle" />
          )}
          {data.solde_requested && data.solde_paid && (
            <StatusBadge label="Interventions reglees" />
          )}
        </div>
      )}

      {/* Info card */}
      <div className="flex flex-col gap-[15px] p-[10px] bg-[#FAFAFA] border border-[#E4E4E4] rounded-[10px]">
        {["carte_grise_manquante", "en_attente_devis", "devis_pret", "devis_supplementaire", "acompte_manquant", "en_attente_depot", "vehicule_depose", "solde_en_attente"].includes(data.tracking_step)
          ? <InfoRow label="Date de dépôt du véhicule" value={data.deposit_date || "À définir"} large />
          : <InfoRow label="Date de restitution" value={data.restitution_date || "À définir"} large />
        }
        {data.org_name && (
          <InfoRow label="Garage" value={data.org_name} />
        )}
        <CopyableAddress value={data.org_address || "—"} />
        <InfoRow
          label="Coordonnées"
          value={[data.org_phone, data.org_email].filter(Boolean).join("\n") || "—"}
        />
      </div>

      {/* "Poser une question" button — opens WhatsApp conversation with the org */}
      {data.org_whatsapp_phone ? (
        <a
          href={`https://wa.me/${data.org_whatsapp_phone.replace(/[^0-9+]/g, "")}?text=${encodeURIComponent(`Bonjour, j'ai une question concernant mon ordre ${data.ordre_num}. Merci !`)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-[10px] py-[15px] bg-[#25D366] border border-[#1DA851] rounded-[10px] shadow-[0_2px_4px_rgba(0,0,0,0.15)] hover:bg-[#22c55e] transition-colors no-underline"
        >
          <span className="font-bold text-[15px] leading-[100%] text-white">
            Poser une question
          </span>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
            <path fillRule="evenodd" d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.5.5 0 0 0 .613.613l4.458-1.495A11.952 11.952 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zM2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12z" />
          </svg>
        </a>
      ) : data.org_phone ? (
        <a
          href={`tel:${data.org_phone}`}
          className="flex items-center justify-center gap-[10px] py-[15px] bg-white border border-[#5EEAD4] rounded-[10px] shadow-[0_2px_4px_rgba(0,0,0,0.15)] hover:bg-neutral-50 transition-colors no-underline"
        >
          <span className="font-bold text-[15px] leading-[100%] text-black">
            Poser une question
          </span>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="#43E4CB">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
            <path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.5.5 0 0 0 .613.613l4.458-1.495A11.952 11.952 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.34 0-4.512-.753-6.277-2.03l-.44-.33-3.095 1.038 1.038-3.095-.33-.44A9.965 9.965 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" />
          </svg>
        </a>
      ) : null}

      {/* Request summary table */}
      <div className="flex flex-col border border-[#E6E6E6] rounded-[15px] bg-[#FBFCFF] py-[10px] overflow-hidden">
        {summaryRows.map((row, i) => (
          <div key={row.label}>
            <div className="flex justify-between items-start px-[10px] py-[1px]">
              <span className="text-[10px] font-medium leading-[15px] text-[#D9D9D9]">
                {row.label}
              </span>
              <span className="text-[10px] font-medium leading-[15px] text-[#A1A1A1] text-right">
                {row.value}
              </span>
            </div>
            {i < summaryRows.length - 1 && (
              <div className="border-b border-[#D9D9D9] my-[5px]" />
            )}
          </div>
        ))}
      </div>

      {/* Mobility options */}
      <div className="flex flex-col gap-[2px] pt-[10px]">
        {MOBILITY_OPTIONS.map((opt) => {
          const isSelected = data.mobility_option === opt.key;
          return (
            <label key={opt.key} className="flex items-center gap-[10px] cursor-default">
              <div
                className="w-[14px] h-[14px] rounded-[3px] border flex items-center justify-center shrink-0"
                style={{
                  background: isSelected ? "#A1A1A1" : "transparent",
                  borderColor: isSelected ? "#A1A1A1" : "#D9D9D9",
                }}
              >
                {isSelected && (
                  <svg width="7" height="5" viewBox="0 0 7 5" fill="none">
                    <path d="M1 2.5L2.5 4L6 1" stroke="white" strokeWidth="1" />
                  </svg>
                )}
              </div>
              <span
                className="text-[10px] font-medium leading-[15px]"
                style={{ color: isSelected ? "#A1A1A1" : "#D9D9D9" }}
              >
                {opt.label}
              </span>
            </label>
          );
        })}
      </div>

      {/* Tire options */}
      <div className="flex flex-col gap-[2px] pt-[10px] pb-5">
        {TIRE_OPTIONS.map((opt) => {
          const isSelected = data.tire_option === opt.key;
          return (
            <label key={opt.key} className="flex items-center gap-[10px] cursor-default">
              <div
                className="w-[14px] h-[14px] rounded-[3px] border flex items-center justify-center shrink-0"
                style={{
                  background: isSelected ? "#A1A1A1" : "transparent",
                  borderColor: isSelected ? "#A1A1A1" : "#D9D9D9",
                }}
              >
                {isSelected && (
                  <svg width="7" height="5" viewBox="0 0 7 5" fill="none">
                    <path d="M1 2.5L2.5 4L6 1" stroke="white" strokeWidth="1" />
                  </svg>
                )}
              </div>
              <span
                className="text-[10px] font-medium leading-[15px]"
                style={{ color: isSelected ? "#A1A1A1" : "#D9D9D9" }}
              >
                {opt.label}
              </span>
            </label>
          );
        })}
      </div>
      {/* Fullscreen carte grise upload overlay */}
      {/* Hidden file input for carte grise */}
      <input
        ref={carteGriseInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleCarteGriseChange}
      />

      {carteGriseFile && ordreId && (
        <CarteGriseUploadOverlay
          file={carteGriseFile}
          ordreId={ordreId}
          onClose={() => setCarteGriseFile(null)}
          onSuccess={() => {
            setCarteGriseFile(null);
            onDataRefresh?.();
          }}
        />
      )}
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────

const RATING_LABELS = ["", "Mauvais", "Décevant", "Correct", "Très bien", "Excellent"];

const CONFETTI_COLORS = ["#5C6BFF", "#43E4CB", "#F59E0B", "#EC4899", "#8B5CF6", "#10B981"];

function RatingBlock({
  hoveredStar,
  setHoveredStar,
  selectedRating,
  setSelectedRating,
  ordreId,
  previewMode,
}: {
  hoveredStar: number;
  setHoveredStar: (v: number) => void;
  selectedRating: number;
  setSelectedRating: (v: number) => void;
  ordreId?: string;
  previewMode?: boolean;
}) {
  const [animPhase, setAnimPhase] = useState<"idle" | "bounce" | "done">("idle");
  const [bounceIndex, setBounceIndex] = useState(-1);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [confettiParticles] = useState(() =>
    Array.from({ length: 24 }, (_, i) => ({
      id: i,
      x: (Math.random() - 0.5) * 200,
      y: -(Math.random() * 120 + 40),
      rotate: Math.random() * 360,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      delay: Math.random() * 200,
      size: Math.random() * 4 + 3,
    }))
  );

  // Le client essaie souvent plusieurs étoiles : seule la dernière, après une
  // pause, est envoyée (chaque envoi publiait un avis + des notifs, OR-1187808).
  const submitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleRate = (star: number) => {
    if (previewMode || animPhase === "bounce") return;
    setSelectedRating(star);
    setAnimPhase("bounce");
    setBounceIndex(0);

    // Cascade bounce through stars
    for (let i = 0; i <= star; i++) {
      setTimeout(() => setBounceIndex(i), i * 80);
    }
    setTimeout(() => {
      setAnimPhase("done");
      setBounceIndex(-1);
      // Confetti for good ratings
      if (star >= 4) {
        setShowConfetti(true);
        setShowThankYou(true);
        setTimeout(() => setShowConfetti(false), 1200);
        setTimeout(() => setShowThankYou(false), 3000);
      }
    }, star * 80 + 300);

    if (submitTimerRef.current) clearTimeout(submitTimerRef.current);
    if (ordreId) submitTimerRef.current = setTimeout(() => {
      submitTrackingRating(ordreId, star).catch(() => {});
    }, 2000);
  };

  const displayRating = hoveredStar || selectedRating;

  return (
    <div className="flex flex-col items-center gap-3 py-8 relative overflow-visible">
      <span className="text-[15px] font-semibold text-black">
        Comment s'est passée votre expérience ?
      </span>

      <div className="flex gap-4 py-3 relative">
        {/* Confetti */}
        {showConfetti && (
          <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 10 }}>
            {confettiParticles.map((p) => (
              <div
                key={p.id}
                className="absolute rounded-full"
                style={{
                  width: p.size,
                  height: p.size,
                  backgroundColor: p.color,
                  left: "50%",
                  top: "50%",
                  animation: `confetti-burst 1s ease-out ${p.delay}ms forwards`,
                  ["--tx" as string]: `${p.x}px`,
                  ["--ty" as string]: `${p.y}px`,
                  ["--rot" as string]: `${p.rotate}deg`,
                  opacity: 0,
                }}
              />
            ))}
          </div>
        )}

        {[1, 2, 3, 4, 5].map((star) => {
          const active = star <= displayRating;
          const isBouncing = animPhase === "bounce" && star - 1 === bounceIndex && star <= selectedRating;
          return (
            <button
              key={star}
              onMouseEnter={() => animPhase !== "bounce" && setHoveredStar(star)}
              onMouseLeave={() => setHoveredStar(0)}
              onClick={() => handleRate(star)}
              className="relative"
              style={{
                transform: isBouncing ? "translateY(-12px) scale(1.3)" : active ? "scale(1)" : "scale(0.9)",
                transition: "transform 250ms cubic-bezier(0.34, 1.56, 0.64, 1)",
              }}
            >
              {/* Glow ring */}
              {active && animPhase === "done" && selectedRating >= 4 && (
                <div
                  className="absolute inset-0 rounded-full"
                  style={{
                    background: "radial-gradient(circle, rgba(92,107,255,0.25) 0%, transparent 70%)",
                    transform: "scale(1.8)",
                    animation: "glow-fade 1.5s ease-out forwards",
                  }}
                />
              )}
              <svg
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill={active ? "#5C6BFF" : "none"}
                stroke={active ? "#5C6BFF" : "#D9D9D9"}
                strokeWidth="1.5"
                style={{
                  transition: "fill 120ms, stroke 120ms",
                  transitionDelay: `${(star - 1) * 40}ms`,
                  filter: active && selectedRating >= 4 ? "drop-shadow(0 0 6px rgba(92,107,255,0.4))" : "none",
                }}
              >
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
            </button>
          );
        })}
      </div>

      <div className="h-6 flex items-center">
        {showThankYou ? (
          <span
            className="text-[14px] font-semibold text-[#5C6BFF]"
            style={{ animation: "slide-up-fade 400ms ease-out" }}
          >
            Merci beaucoup !
          </span>
        ) : displayRating > 0 ? (
          <span className="text-[13px] font-medium text-[#5C6BFF]">
            {RATING_LABELS[displayRating]}
          </span>
        ) : null}
      </div>

      <style>{`
        @keyframes confetti-burst {
          0% { opacity: 1; transform: translate(0, 0) rotate(0deg) scale(1); }
          100% { opacity: 0; transform: translate(var(--tx), var(--ty)) rotate(var(--rot)) scale(0.5); }
        }
        @keyframes glow-fade {
          0% { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes slide-up-fade {
          0% { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

function CopyableAddress({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const didLongPress = useRef(false);

  const openInMaps = useCallback(() => {
    const q = encodeURIComponent(value);
    window.open(`https://www.google.com/maps/search/?api=1&query=${q}`, "_blank");
  }, [value]);

  const copyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [value]);

  const onPointerDown = () => {
    didLongPress.current = false;
    longPressTimer.current = setTimeout(() => {
      didLongPress.current = true;
      openInMaps();
    }, 600);
  };

  const onPointerUp = () => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
  };

  if (value === "—") {
    return <InfoRow label="Adresse" value="—" />;
  }

  return (
    <div className="flex flex-col gap-[2px]">
      <span className="text-[11px] font-medium leading-4 text-[#A1A1A1]">Adresse</span>
      <div
        className="flex items-start justify-between gap-2 cursor-pointer select-none"
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onClick={() => { if (!didLongPress.current) copyToClipboard(); }}
      >
        <span className="font-medium leading-4 text-[#5C6BFF] whitespace-pre-line" style={{ fontSize: 11 }}>
          {value}
        </span>
        <div className="shrink-0 mt-[1px]">
          {copied ? (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="#5C6BFF">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
            </svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="#A1A1A1">
              <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
            </svg>
          )}
        </div>
      </div>
      <span className="text-[9px] text-[#D9D9D9] leading-3">
        {copied ? "Copié !" : "Appui long pour ouvrir dans Maps"}
      </span>
    </div>
  );
}

function StatusBadge({ label, onClick, pending }: { label: string; onClick?: () => void; pending?: boolean }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      onClick={onClick}
      className={`flex items-center gap-[6px] py-[8px] px-[12px] rounded-[8px] border ${pending ? "border-[#F59E0B]/30 bg-[#FFFBEB]" : "border-[#E4E4E4] bg-[#FAFAFA]"} ${onClick ? "cursor-pointer hover:bg-[#F0F0F0] transition-colors" : ""}`}
    >
      {pending ? (
        <svg width="12" height="12" viewBox="0 0 16 16" fill="#F59E0B">
          <circle cx="8" cy="8" r="6" fill="none" stroke="#F59E0B" strokeWidth="1.5" />
          <path d="M8 4v4.5l3 1.5" stroke="#F59E0B" strokeWidth="1.5" fill="none" strokeLinecap="round" />
        </svg>
      ) : (
        <svg width="12" height="12" viewBox="0 0 16 16" fill="#5C6BFF">
          <path d="M6.5 11.5L3 8l1-1 2.5 2.5L12 4l1 1-6.5 6.5z" />
        </svg>
      )}
      <span className={`text-[11px] font-medium ${pending ? "text-[#F59E0B]" : "text-[#5C6BFF]"}`}>{label}</span>
      {onClick && (
        <div className="ml-auto flex items-center">
          <Icon1 name="open" size={14} color="#A1A1A1" />
        </div>
      )}
    </Tag>
  );
}

function InfoRow({ label, value, large }: { label: string; value: string; large?: boolean }) {
  return (
    <div className="flex flex-col gap-[2px]">
      <span className="text-[11px] font-medium leading-4 text-[#A1A1A1]">{label}</span>
      <span
        className={`font-${large ? "bold" : "medium"} leading-${large ? "[100%]" : "4"} text-[#5C6BFF] whitespace-pre-line`}
        style={{ fontSize: large ? 20 : 11 }}
      >
        {value}
      </span>
    </div>
  );
}
